/**
 * SearchInput — wraps DS Input with type="search", a search icon via iconLeft,
 * and an optional clear button via iconRight.
 *
 * ~30 lines of actual logic. Replaces page-local SearchField definitions.
 */
import {X, Search} from 'lucide-react'
import React, {forwardRef} from 'react'
import styled from 'styled-components'
import {Input} from '~/src/design-system/Primitives'
import type {InputProps} from '~/src/design-system/Primitives'

export interface SearchInputProps extends Omit<InputProps, 'type' | 'iconLeft'> {
  /** Show clear button when value is non-empty */
  onClear?: () => void
  /** Current value — used to determine if clear button shows */
  value?: string
}

/**
 * type="search" inputs render a native WebKit clear (×) button whenever they
 * hold a value — which sits right next to our own DS clear button (two X's,
 * the native one only while focused). Suppress the native one so only the DS
 * clear shows. The selectors cover the styled class landing on the input itself
 * or on the Input wrapper.
 */
const SearchInputBase = styled(Input)`
  &::-webkit-search-cancel-button,
  &::-webkit-search-decoration,
  & input::-webkit-search-cancel-button,
  & input::-webkit-search-decoration {
    -webkit-appearance: none;
    appearance: none;
    display: none;
  }
`

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({onClear, value, iconRight, ...rest}, ref) => {
    const showClear = Boolean(value && onClear)

    return (
      <SearchInputBase
        ref={ref}
        type="search"
        iconLeft={<Search size={16} />}
        iconRight={
          showClear ? (
            <button
              type="button"
              onClick={onClear}
              aria-label="Clear search"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'inline-flex',
                color: 'inherit',
              }}>
              <X size={16} />
            </button>
          ) : (
            iconRight
          )
        }
        value={value}
        {...rest}
      />
    )
  },
)
SearchInput.displayName = 'SearchInput'

export default SearchInput
